import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Select, CloseButton, Heading, Textarea, Spinner } from "@chakra-ui/react";

import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Editor from "components/Editor/Editor";
import React, { useRef, useState, useEffect } from "react";
import { BsCircleFill } from "react-icons/bs";
import { countries as countriesList } from "countries-list";

const data = [
  { label: "Purchase Agreement", value: "Purchase Agreement" },
  { label: "Lease Agreement", value: "Lease Agreement" },
  { label: "Employment Agreement", value: "Employment Agreement" },
  { label: "Partnership Agreement", value: "Partnership Agreement" },
  { label: "Joint Venture Agreement", value: "Joint Venture Agreement" },
  { label: "Non-Disclosure Agreement (NDA)", value: "Non-Disclosure Agreement (NDA)" },
  { label: "Non-Compete Agreement", value: "Non-Compete Agreement" },
  { label: "Service Agreement", value: "Service Agreement" },
  { label: "Licensing Agreement", value: "Licensing Agreement" },
  { label: "Loan Agreement", value: "Loan Agreement" },
  { label: "Sales Agreement", value: "Sales Agreement" },
  { label: "Franchise Agreement", value: "Franchise Agreement" },
  { label: "Confidentiality Agreement", value: "Confidentiality Agreement" },
  { label: "Distribution Agreement", value: "Distribution Agreement" },
  { label: "Settlement Agreement", value: "Settlement Agreement" },
  { label: "Insurance Policy", value: "Insurance Policy" },
  { label: "Mergers and Acquisition Agreement", value: "Mergers and Acquisition Agreement" },
  { label: "Intellectual Property Agreement", value: "Intellectual Property Agreement" },
  { label: "Supply Agreement", value: "Supply Agreement" },
  { label: "Agency Agreement", value: "Agency Agreement" },
];

const PartyList = ({ partiesList, handleDelete }) => (
  <Stack>
    {partiesList.map((party, index) => (
      <Flex direction="row" justify="space-between" align="center" mb="20px" key={index}>
        <Heading size="md">{party.name}</Heading>
        <CloseButton size="md" onClick={() => handleDelete(index)} />
      </Flex>
    ))}
  </Stack>
);

const PartyForm = ({
  isAddingParties,
  partyName,
  setPartyName,
  email,
  setEmail,
  isInputValid,
  companyName,
  setCompanyName,
  address,
  setAddress,
  handleAddParty,
}) => (
  <Stack direction="column" spacing="20px" w="100%">
    {isAddingParties ? (
      <Stack w="100%">
        <FormControl isInvalid={!isInputValid.partyName}>
          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
            Party's Name
          </FormLabel>
          <Input
            placeholder="eg. Joe Dey"
            autoCapitalize="none"
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
            Email
          </FormLabel>
          <Input
            placeholder="eg. xyz@gmail.com"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormControl>
        {email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) && (
          <Text color="red">Please enter a valid email</Text>
        )}
        <FormControl>
          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
            Company Name (optional)
          </FormLabel>
          <Input
            placeholder="eg. Corporation"
            autoCapitalize="none"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
            Party's Address (optional)
          </FormLabel>
          <Textarea
            placeholder="eg. Tokyo"
            autoCapitalize="none"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3} // You can adjust the number of rows as needed
          />
        </FormControl>
        <Button
          variant="dark"
          w={{ sm: "100%", lg: "100%" }}
          h="45px"
          mt="24px"
          onClick={handleAddParty}
          isDisabled={partyName === "" || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
          style={{ opacity: partyName ? 1 : 0.7 }}
        >
          <Text fontSize="lg" fontWeight="bold">
            Save
          </Text>
        </Button>
      </Stack>
    ) : (
      <Button
        onClick={() => setIsAddingParties(true)}
        style={{ opacity: partyName ? 1 : 0.7 }}
        mt="4px"
      >
        <Text>Add Parties +</Text>
      </Button>
    )}
  </Stack>
);

const SelectType = ({
  handleSelectChange,
  selectedOption,
  handleCountryChange,
  selectedCountry,
  data,
}) => {
  const countries = Object.values(countriesList).map((country) => country.name);

  return (
    <Flex direction="column" w="100%">
      <Flex direction={{ sm: "column", md: "row" }} w="100%" mb="24px">
        <Stack direction="column" spacing="20px" w="100%">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="bold">
              Type of agreement/contract
            </FormLabel>
            <Select
              value={selectedOption}
              onChange={handleSelectChange}
              placeholder="Select an option"
            >
              {data.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="bold">
              Country
            </FormLabel>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              placeholder="Select a country"
            >
              <option value="" disabled>
                Select a country
              </option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Flex>
    </Flex>
  );
};

const tabData = [
  {
    label: "Select Type",
    title: "Let's start with the basic information",
    subtitle: "Let us know your agreement type and country.",
    content: (props) => <SelectType {...props} />,
    disabled: false,
  },
  {
    label: "Add Party",
    title: "Add Parties",
    subtitle: "",
    content: (props) => (
      <Stack>
        <PartyList {...props} />
        <PartyForm {...props} />
      </Stack>
    ),
    disabled: true,
  },
  // { label: "Create", content: "Generate Agreement", disabled: true },
];

function Create() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgPrevButton = useColorModeValue("gray.100", "gray.100");
  const iconColor = useColorModeValue("gray.300", "gray.700");

  const [isAddingParties, setIsAddingParties] = useState(false);
  const [partyName, setPartyName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [partiesList, setPartiesList] = useState([]);
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [createAgreement, setCreateAgreement] = useState(``);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [isGeneratingAgreement, setIsGeneratingAgreement] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  const [isInputValid, setIsInputValid] = useState({
    partyName: true,
    companyName: true,
    email: true,
    address: true,
  });

  const [activeBullets, setActiveBullets] = useState({
    partyName: true,
    companyName: false,
    email: false,
  });

  const aboutTab = useRef();
  const accountTab = useRef();
  const addressTab = useRef();

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleSelectChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const validateInput = () => {
    const isPartyNameValid = partyName.trim() !== "";
    setIsInputValid(isPartyNameValid);
  };

  const handleDelete = (index) => {
    let newPartiesList = [...partiesList];
    newPartiesList.splice(index, 1);
    setPartiesList(newPartiesList);
    console.log("delete");
  };

  const handleAddParty = () => {
    if (!isInputValid.partyName || !isInputValid.companyName || !isInputValid.email) {
      return;
    }

    if (isInputValid) {
      const newParty = {
        name: partyName,
        company: companyName,
        address: address,
        country: selectedCountry,
        email: email,
      };
      setPartiesList([...partiesList, newParty]);
      setPartyName("");
      setCompanyName("");
      setAddress("");
      setEmail("");
      setSelectedCountry("");
      setIsAddingParties(false);
    }
  };
  const sendToOpenAI = async (agreementText) => {
    try {
      const response = await fetch("https://api-seyimfnmxq-uc.a.run.app/generateDocument", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: agreementText,
          max_tokens: 200,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreateAgreement(data.data);
        console.log("Generated Agreement:", JSON.stringify(data));
      } else {
        console.error("Failed to generate agreement. Response status:", response.status);
      }
    } catch (error) {
      console.error("Error sending request to OpenAI:", error);
    }
    console.log("console", createAgreement);
  };

  const handleCreateAgreement = () => {
    setIsGeneratingAgreement(true);
    const agreementText = generateAgreement();
    sendToOpenAI(agreementText).then(() => {
      setIsGeneratingAgreement(false);
      addressTab.current.click();
    });
  };
  const generateAgreement = () => {
    const agreementType = selectedOption || "Type of Agreement";

    const partiesInfo = partiesList.map((party, index) => {
      return `### PARTY ${index + 1}:
- Name: ${party.name}
- Company Name: ${party.company}
- Email: ${party.email || "N/A"}
- Address: ${party.address || "N/A"}
`;
    });

    const partiesText = partiesInfo.join("\n");

    const currentDate = new Date().toLocaleDateString();

    // Create the content prompt for generating the agreement using Markdown formatting
    const contentPrompt = `
    This **${agreementType}** is entered into on **${currentDate}** by and between the undersigned parties.
    ##Country: ${selectedCountry}
    ##Agreement Type: ${agreementType}
    ##Date: ${currentDate}

${partiesText}

---

Given the information provided and incorporating the current date, please draft detailed terms and conditions appropriate for a ${agreementType} in the context of ${selectedCountry} law. The document in the markdown format with valid separators without any additional text and use markdown syntax for headings, bullet points,Automatically fill details shared above and no need to give fields for signature or date input.

---


`;

    return contentPrompt;
  };

  const [tabIndex, setTabIndex] = useState(0);

  const onNext = () => {
    if (tabIndex < tabData.length - 1) {
      setTabIndex((prev) => prev + 1);
    }
  };

  const onBack = () => {
    if (tabIndex > 0) {
      setTabIndex((prev) => prev - 1);
    }
  };

  return (
    <Flex direction="column" minH="100vh" align="center" pt={{ sm: "125px", lg: "75px" }} mt="8em">
      <Flex direction="column" textAlign="center" mb={{ sm: "25px", md: "45px" }}>
        <Text
          color="white"
          fontSize={{ sm: "2xl", md: "3xl", lg: "4xl" }}
          fontWeight="bold"
          mb="8px"
        >
          Generate Agreement with Cogni Signer AI{" "}
        </Text>
        <Text color="gray.400" fontWeight="normal" fontSize={{ sm: "sm", md: "lg" }}>
          Complete the provided details to craft a compelling agreement.
        </Text>
      </Flex>
      <Tabs
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        variant="unstyled"
        mt="24px"
        display="flex"
        flexDirection="column"
      >
        <TabList display="flex" align="center" alignSelf="center" justifySelf="center">
          {tabData.map((tab, index) => (
            <Tab key={index} isDisabled={index > tabIndex}>
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {tabData.map((tab, index) => (
            <TabPanel w={{ sm: "330px", md: "700px", lg: "850px" }} mx="auto" key={index}>
              <Card>
                <CardHeader>
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    textAlign="center"
                    w="80%"
                    mx="auto"
                  >
                    <Text fontSize="lg" fontWeight="bold" mb="4px">
                      {tab.title}
                    </Text>
                    <Text color="gray.400" fontWeight="normal" fontSize="sm">
                      {tab.subtitle}
                    </Text>
                  </Flex>
                </CardHeader>
                <CardBody>
                  {tab.content({
                    handleSelectChange,
                    selectedOption,
                    handleCountryChange,
                    selectedCountry,
                    data,
                    isAddingParties,
                    partyName,
                    setPartyName,
                    email,
                    setEmail,
                    isInputValid,
                    companyName,
                    setCompanyName,
                    address,
                    setAddress,
                    handleAddParty,
                  })}
                  <Flex direction="row" justifyContent="space-between">
                    <Button onClick={onBack} disabled={tabIndex === 0}>
                      Back
                    </Button>
                    <Button onClick={onNext} disabled={tabIndex === tabData.length - 1}>
                      {tabIndex === tabData.length - 1 ? "Finish" : "Next"}
                    </Button>
                  </Flex>
                </CardBody>
              </Card>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Flex>
  );

  return (
    <Flex direction="column" minH="100vh" align="center" pt={{ sm: "125px", lg: "75px" }}>
      <Flex direction="column" textAlign="center" mb={{ sm: "25px", md: "45px" }}>
        <Text
          color={textColor}
          fontSize={{ sm: "2xl", md: "3xl", lg: "4xl" }}
          fontWeight="bold"
          mb="8px"
        >
          Generate Agreement with Cogni Signer AI{" "}
        </Text>
        <Text color="gray.400" fontWeight="normal" fontSize={{ sm: "sm", md: "lg" }}>
          Complete the provided details to craft a compelling agreement.
        </Text>
      </Flex>
      <Tabs variant="unstyled" mt="24px" display="flex" flexDirection="column">
        <TabList display="flex" align="center" alignSelf="center" justifySelf="center">
          <Tab
            ref={aboutTab}
            _focus={{}}
            w={{ sm: "120px", md: "250px", lg: "300px" }}
            onClick={() => {
              setActiveBullets({
                about: false,
                account: false,
                address: false,
              });
            }}
          >
            <Flex
              direction="column"
              justify="center"
              align="center"
              position="relative"
              _before={{
                content: "''",
                width: { sm: "120px", md: "250px", lg: "300px" },
                height: "3px",
                bg: activeBullets.account ? "white" : "blue.300",
                left: { sm: "12px", md: "30px" },
                top: {
                  sm: activeBullets.about ? "6px" : "4px",
                  md: null,
                },
                position: "absolute",
                bottom: activeBullets.about ? "40px" : "38px",

                transition: "all .3s ease",
              }}
            >
              <Icon
                zIndex="1"
                as={BsCircleFill}
                color={activeBullets.about ? "white" : "blue.300"}
                w={activeBullets.about ? "16px" : "12px"}
                h={activeBullets.about ? "16px" : "12px"}
                mb="8px"
              />
              <Text
                color={activeBullets.about ? "white" : "gray.300"}
                fontWeight={activeBullets.about ? "bold" : "normal"}
                display={{ sm: "none", md: "block" }}
              >
                Select Type
              </Text>
            </Flex>
          </Tab>
          <Tab
            ref={accountTab}
            _focus={{}}
            w={{ sm: "120px", md: "250px", lg: "300px" }}
            onClick={() => {
              setActiveBullets({
                about: true,
                account: false,
                address: false,
              });
            }}
            isDisabled={selectedOption === ""}
          >
            <Flex
              direction="column"
              justify="center"
              align="center"
              position="relative"
              _before={{
                content: "''",
                width: { sm: "120px", md: "250px", lg: "300px" },
                height: "3px",
                bg: activeBullets.address ? "white" : "blue.300",
                left: { sm: "12px", md: "32px" },
                top: {
                  sm: activeBullets.account ? "6px" : "4px",
                  md: null,
                },
                position: "absolute",
                bottom: activeBullets.account ? "40px" : "38px",

                transition: "all .3s ease",
              }}
            >
              <Icon
                zIndex="1"
                as={BsCircleFill}
                color={activeBullets.account ? "white" : "blue.300"}
                w={activeBullets.account ? "16px" : "12px"}
                h={activeBullets.account ? "16px" : "12px"}
                mb="8px"
              />
              <Text
                color={activeBullets.account ? "white" : "gray.300"}
                fontWeight={activeBullets.account ? "bold" : "normal"}
                display={{ sm: "none", md: "block" }}
              >
                Add
              </Text>
            </Flex>
          </Tab>
          <Tab
            ref={addressTab}
            _focus={{}}
            w={{ sm: "120px", md: "250px", lg: "300px" }}
            onClick={() => {
              setActiveBullets({
                about: true,
                account: false,
                address: false,
              });
            }}
            isDisabled={partyName === ""}
          >
            <Flex direction="column" justify="center" align="center" position="relative">
              <Icon
                zIndex="1"
                as={BsCircleFill}
                color={activeBullets.address ? "white" : "blue.300"}
                w={activeBullets.address ? "16px" : "12px"}
                h={activeBullets.address ? "16px" : "12px"}
                mb="8px"
              />
              <Text
                color={activeBullets.address ? "white" : "gray.300"}
                fontWeight={activeBullets.address ? "bold" : "normal"}
                display={{ sm: "none", md: "block" }}
              >
                Create
              </Text>
            </Flex>
          </Tab>
        </TabList>
        <TabPanels mt="24px" maxW={{ md: "90%", lg: "100%" }} mx="auto">
          <TabPanel w={{ sm: "330px", md: "700px", lg: "850px" }} mx="auto">
            <Card>
              <CardHeader mb="40px">
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  textAlign="center"
                  w="80%"
                  mx="auto"
                >
                  <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4px">
                    Let's start with the basic information
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    Let us know your agreement type and country.
                  </Text>
                </Flex>
              </CardHeader>
              <CardBody>
                <Flex direction="column" w="100%">
                  <Flex direction={{ sm: "column", md: "row" }} w="100%" mb="24px">
                    <Stack direction="column" spacing="20px" w="100%">
                      <FormControl>
                        <FormLabel color={textColor} fontSize="xs" fontWeight="bold">
                          Type of agreement/contract
                        </FormLabel>
                        <Select
                          value={selectedOption}
                          onChange={handleSelectChange}
                          placeholder="Select an option"
                        >
                          {data.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel color={textColor} fontSize="xs" fontWeight="bold">
                          Country
                        </FormLabel>
                        <Select
                          value={selectedCountry}
                          onChange={handleCountryChange}
                          placeholder="Select a country"
                        >
                          <option value="" disabled>
                            Select a country
                          </option>
                          {countries.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Flex>
                  <Button
                    variant="dark"
                    alignSelf="flex-end"
                    mt="24px"
                    w={{ sm: "75px", lg: "100px" }}
                    h="35px"
                    onClick={() => accountTab.current.click()}
                    style={{ opacity: selectedOption ? 1 : 0.6 }}
                  >
                    <Text fontSize="xs" color="#fff" fontWeight="bold">
                      NEXT
                    </Text>
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel w={{ sm: "330px", md: "700px", lg: "850px" }} mx="auto">
            <Card>
              <CardHeader mb="40px">
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  textAlign="center"
                  w="80%"
                  mx="auto"
                >
                  <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4px">
                    Choose the involved parties and effortlessly input their agreement details.{" "}
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    Include party details such as name*, email*, company*, and address
                  </Text>
                </Flex>
              </CardHeader>
              <CardBody>
                <Stack>
                  {partiesList.map((party, index) => (
                    <Flex
                      direction="row"
                      justify="space-between"
                      align="center"
                      mb="20px"
                      key={index}
                    >
                      <Heading size="md">{` ${party.name}`}</Heading>
                      <CloseButton size="md" onClick={() => handleDelete(index)} />
                    </Flex>
                  ))}
                </Stack>
                <Flex direction="column" w="100%">
                  <Stack direction="column" spacing="20px" w="100%">
                    {isAddingParties ? (
                      <Stack w="100%">
                        <FormControl isInvalid={!isInputValid.partyName}>
                          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
                            Party's Name
                          </FormLabel>
                          <Input
                            placeholder="eg. Joe Dey"
                            autoCapitalize="none"
                            value={partyName}
                            onChange={(e) => setPartyName(e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
                            Email
                          </FormLabel>
                          <Input
                            placeholder="eg. xyz@gmail.com"
                            autoCapitalize="none"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </FormControl>
                        {email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) && (
                          <Text color="red">Please enter a valid email</Text>
                        )}
                        <FormControl>
                          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
                            Company Name (optional)
                          </FormLabel>
                          <Input
                            placeholder="eg. Corporation"
                            autoCapitalize="none"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color="gray.700" fontSize="xs" fontWeight="bold">
                            Party's Address (optional)
                          </FormLabel>
                          <Textarea
                            placeholder="eg. Tokyo"
                            autoCapitalize="none"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            rows={3} // You can adjust the number of rows as needed
                          />
                        </FormControl>
                        <Button
                          variant="dark"
                          w={{ sm: "100%", lg: "100%" }}
                          h="45px"
                          mt="24px"
                          onClick={handleAddParty}
                          isDisabled={
                            partyName === "" || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
                          }
                          style={{ opacity: partyName ? 1 : 0.7 }}
                        >
                          <Text fontSize="lg" fontWeight="bold">
                            Save
                          </Text>
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        onClick={() => setIsAddingParties(true)}
                        style={{ opacity: partyName ? 1 : 0.7 }}
                        mt="4px"
                      >
                        <Text>Add Parties +</Text>
                      </Button>
                    )}
                  </Stack>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm"></Text>
                  <Flex justify="space-between">
                    <Button
                      variant="no-effects"
                      bg={bgPrevButton}
                      alignSelf="flex-end"
                      mt="24px"
                      w={{ sm: "75px", lg: "100px" }}
                      h="35px"
                      onClick={() => aboutTab.current.click()}
                    >
                      <Text fontSize="xs" color="gray.700" fontWeight="bold">
                        PREV
                      </Text>
                    </Button>
                    <Button
                      variant="dark"
                      alignSelf="flex-end"
                      mt="24px"
                      h="35px"
                      onClick={() => handleCreateAgreement()}
                      style={{ opacity: partiesList.length >= 2 ? 1 : 0.7 }}
                    >
                      <Text fontSize="xs" color="#fff" fontWeight="bold">
                        GENERATE
                      </Text>
                    </Button>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel w={{ sm: "330px", md: "700px", lg: "850px" }} mx="auto">
            <Card>
              <CardHeader mb="40px">
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  textAlign="center"
                  w="80%"
                  mx="auto"
                >
                  <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4px">
                    "Here, you'll receive the generated agreement."{" "}
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    "We appreciate your patience and the invaluable support you've provided!"
                  </Text>
                </Flex>
              </CardHeader>
              <CardBody>
                <Flex direction="column" w="100%">
                  <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4px">
                    Generated Agreement:
                  </Text>
                  {isGeneratingAgreement ? (
                    <Stack>
                      <Spinner size="sm" color="blue.500" />
                      <Text>
                        "Stay tuned while our AI system generates the agreement for you."{" "}
                      </Text>
                    </Stack>
                  ) : (
                    <Stack data-color-mode="light">
                      {/* <MDEditor.Markdown source={createAgreement} /> */}
                    </Stack>
                  )}

                  <Flex justify="space-between">
                    <Button
                      variant="no-effects"
                      bg={bgPrevButton}
                      alignSelf="flex-end"
                      mt="24px"
                      h="35px"
                      onClick={() => {
                        setCreateAgreement("");
                        accountTab.current.click();
                      }}
                    >
                      <Text fontSize="xs" color="gray.700" fontWeight="bold">
                        RE-GENRATE
                      </Text>
                    </Button>
                    <Button
                      variant="dark"
                      alignSelf="flex-end"
                      mt="24px"
                      w={{ sm: "75px", lg: "100px" }}
                      h="35px"
                    >
                      <Text fontSize="xs" color="#fff" fontWeight="bold">
                        E-SIGN
                      </Text>
                    </Button>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}

export default Create;
